import React, { useState, useEffect, useRef } from 'react';

const SESSION_KEY = 'compudopt_local_auth';

// The embed route is served inside iframes on partner sites to end users —
// it must never be blocked by the internal team passphrase.
const PUBLIC_PATHS = ['/embed'];

function isPublicPath() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  return PUBLIC_PATHS.includes(path);
}

export default function PassphraseGate({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Check session on mount
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY) === 'true') {
      setAuthenticated(true);
    }
    setChecking(false);
  }, []);

  // Auto-focus input when gate appears
  useEffect(() => {
    if (!checking && !authenticated && inputRef.current) {
      inputRef.current.focus();
    }
  }, [checking, authenticated]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!passphrase.trim() || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const apiBase = window.location.origin;
      const response = await fetch(`${apiBase}/api/verify-passphrase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passphrase: passphrase.trim() }),
      });

      const data = await response.json();

      if (data.ok) {
        sessionStorage.setItem(SESSION_KEY, 'true');
        // Admin token for authenticated API calls (issues-board PATCH)
        if (data.token) {
          sessionStorage.setItem('compudopt_admin_token', data.token);
        }
        // Stay on the page the user originally asked for (deep links like
        // /admin or /local must survive authentication)
        setAuthenticated(true);
      } else {
        setError(data.error || 'Incorrect passphrase');
        setPassphrase('');
        if (inputRef.current) inputRef.current.focus();
      }
    } catch (err) {
      setError('Unable to verify — check your connection');
    } finally {
      setSubmitting(false);
    }
  }

  // Public routes (embedded widget) bypass the gate entirely
  if (isPublicPath()) return children;

  // Still checking session
  if (checking) return null;

  // Authenticated — render the page
  if (authenticated) return children;

  // Passphrase gate UI
  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-secondary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--space-5)',
    }}>
      <div style={{
        background: 'var(--bg-primary)',
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--gray-200)',
        padding: 'var(--space-8)',
        width: '100%',
        maxWidth: '400px',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-6)' }}>
          <h1 style={{
            color: 'var(--text-primary)',
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-bold)',
            margin: '0 0 var(--space-2) 0',
          }}>
            Compudopt AI
          </h1>
          <p style={{
            color: 'var(--text-tertiary)',
            fontSize: 'var(--text-sm)',
            margin: 0,
          }}>
            Enter the team passphrase to continue
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: 'var(--error-bg)',
            border: '1px solid var(--error-border)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-3)',
            marginBottom: 'var(--space-4)',
            fontSize: 'var(--text-sm)',
            color: 'var(--error)',
            textAlign: 'center',
          }}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            placeholder="Passphrase"
            disabled={submitting}
            autoComplete="off"
            style={{
              width: '100%',
              padding: 'var(--space-3)',
              border: '1px solid var(--gray-300)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 'var(--text-base)',
              fontFamily: 'inherit',
              color: 'var(--text-primary)',
              background: 'var(--bg-primary)',
              outline: 'none',
              boxSizing: 'border-box',
              marginBottom: 'var(--space-4)',
              transition: 'border-color var(--duration-normal) var(--ease-out)',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--compudopt-green)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'var(--gray-300)'; }}
          />

          <button
            type="submit"
            disabled={submitting || !passphrase.trim()}
            style={{
              width: '100%',
              padding: 'var(--space-3)',
              background: submitting || !passphrase.trim() ? 'var(--gray-400)' : 'var(--compudopt-green)',
              color: 'var(--text-on-primary)',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-semibold)',
              cursor: submitting || !passphrase.trim() ? 'not-allowed' : 'pointer',
              transition: 'all var(--duration-normal) var(--ease-out)',
            }}
            onMouseEnter={(e) => {
              if (!submitting && passphrase.trim()) {
                e.currentTarget.style.background = 'var(--green-dark)';
              }
            }}
            onMouseLeave={(e) => {
              if (!submitting && passphrase.trim()) {
                e.currentTarget.style.background = 'var(--compudopt-green)';
              }
            }}
          >
            {submitting ? 'Verifying...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
